/**
 * Storage utilities for handling localStorage safely in SSR environments
 */

/**
 * Gets a value from localStorage with SSR safety
 * @param key The key to get from localStorage
 * @returns The value or null if not found
 */
export const getFromStorage = (key: string): string | null => {
  if (typeof window === 'undefined') return null;
  
  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.error(`Error getting ${key} from localStorage:`, error);
    return null;
  }
};

/**
 * Sets a value in localStorage with SSR safety
 * @param key The key to set in localStorage
 * @param value The value to set
 */
export const setInStorage = (key: string, value: string): void => {
  if (typeof window === 'undefined') return;
  
  try {
    localStorage.setItem(key, value);
  } catch (error) {
    console.error(`Error setting ${key} in localStorage:`, error);
  }
};

/**
 * Removes a value from localStorage with SSR safety
 * @param key The key to remove from localStorage
 */
export const removeFromStorage = (key: string): void => {
  if (typeof window === 'undefined') return;
  
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error(`Error removing ${key} from localStorage:`, error);
  }
};

/**
 * Gets the authentication token from localStorage
 * @returns The authentication token or null if not found
 */
export const getAuthToken = (): string | null => {
  return getFromStorage('auth_token');
};

/**
 * Sets the authentication token in localStorage
 * @param token The authentication token to set
 */
export const setAuthToken = (token: string): void => {
  setInStorage('auth_token', token);
};

/**
 * Gets the user ID from localStorage
 * @returns The user ID or null if not found
 */
export const getUserId = (): string | null => {
  return getFromStorage('user_id') || getFromStorage('userId');
};

/**
 * Sets the user ID in localStorage
 * @param userId The user ID to set
 */
export const setUserId = (userId: string): void => {
  setInStorage('user_id', userId);
  // Also set userId for backwards compatibility
  setInStorage('userId', userId);
}; 